// Sugalarm — IOB countdown rings. Ported from screens.jsx BolusRings.
//
// Rapid rings: outer = oldest still-active bolus, inner = newest. Filled
// arc = remaining fraction; track behind = elapsed. Center number is
// total RAPID IOB in units. Per-bolus arc colors come from the caller
// (`b.color`) — NowScreen sources them from `isCorrectionBolus` so a
// correction ring reads amber and a meal ring green (cycle 74b color
// coding; cycle 75 keeps the rings per-bolus while regrouping lines).
//
// Cycle 147: long-acting (basal) insulin is a THIRD, OUTERMOST ring —
// promoted off the retired cycle-121 `BasalLine` 24-segment meter. Its
// arc is `basalForContext().remainingFrac` (the single basal-decay
// source — no second computation here), colored BASAL_RING_COLOR
// (--iob-ring-third, household-recolorable) and amber (--zone-warn)
// when due. Basal stays EXCLUDED from the rapid rings, the center IOB
// total, and the countdowns — the ring-exclusion invariant is
// untouched; this is an additive arc. With no basal on board the
// two-ring layout renders byte-identical to pre-147. Basal-only (no
// rapid IOB) shows just the basal ring with the long-acting remaining
// in the center.
//
// Cycle 74b: the card mounts permanently on surfaces that show it. When
// no bolus is active the rings render as a dimmed empty state (0.0 /
// I.O.B, no arc), and the dose-lines area is supplied by the caller via
// `children` (a <MealContextLines>).
//
// Cycle 150: `bare` drops the card surface entirely (clock-face mount),
// and in `hideContext` mode the basal ring's countdown joins the center
// readout (colored to the ring) — on context-bearing surfaces the basal
// time stays on its Long-acting row and the center is unchanged.

import { useState } from "react";
import type { ReactNode } from "react";

import { useNow } from "../../hooks/useNow";
import { MaskedScroll } from "../ui/MaskedScroll";
import type { BasalContext, BolusForRings } from "../../lib/iob";
import {
  basalRingColor,
  formatBasalHoursLeft,
  iobTimeRemainingMs,
  remainingFraction,
  ringLabelInk,
  totalIOB,
} from "../../lib/iob";

interface Props {
  boluses: BolusForRings[];
  now: number;
  /** Cycle 147: the long-acting dose on board (basalForContext result) —
   *  null/undefined → no basal ring, the pre-147 two-ring layout. */
  basal?: BasalContext | null;
  /** Cycle 42: when provided, the whole panel becomes a one-tap path
   *  into the editor modal. When omitted the panel renders exactly as
   *  before — no tappable affordance. */
  onTap?: () => void;
  /** Cycle 163.1: renders a glass × in the card's top-right when the
   *  basal is in its DUE state — dismisses the due nudge (the caller
   *  owns the dismissal state). Never passed on the clock-face mount. */
  onDismissDue?: () => void;
  /** Cycle 303: renders the glass × that dismisses the WHOLE IOB card
   *  (server-coordinated via `iob_card_dismissed_at`). Mutually exclusive
   *  with the due-× above: when a basal is DUE the due-nudge × takes the
   *  corner (a due basal is never dismissed into silence by the card ×).
   *  Never passed on the clock-face mount. */
  onDismissCard?: () => void;
  /** Cycle 74b: the dose-lines block beside the rings — a
   *  <MealContextLines>. Replaces the old inline free-text fragment. */
  children?: ReactNode;
  /** Cycle 115: rings-only mode for the slim mobile-landscape stats
   *  column — drops the entire side column (the "Insulin on board"
   *  label + `children`) so the card shrinks to just the rings + their
   *  center readout. Default false → every existing mount is unchanged. */
  hideContext?: boolean;
  /** Cycle 122.1: stack the rings ABOVE the meal/context column (vs the
   *  default side-by-side row) — used by the mobile-portrait pager page.
   *  Default false → kiosk / web / landscape unchanged. */
  stack?: boolean;
  /** Cycle 150: render WITHOUT the elevated card surface (no --bg-elev
   *  panel, border, or padding) so the rings sit directly on the
   *  surrounding ground — the bedside clock-face treatment. Default
   *  false → every existing mount keeps the card exactly as shipped. */
  bare?: boolean;
  /** Cycle 227 (mobile-portrait Now only): a CSS-module class applied to
   *  the card root in place of the inline `--bg-elev`/border surface — lets
   *  the caller paint the card as a glass panel (--card-glass-* tokens +
   *  backdrop-filter). When set, the inline background + border are omitted
   *  so the class owns the surface. Default undefined → every other mount
   *  renders the shipped solid card. */
  cardClassName?: string;
  /** Cycle 227 (mobile-portrait Now only): the card fills + can SHRINK
   *  within a height-constrained flex parent (rings stay fixed; the detail
   *  region gives up height). Default false → byte-identical natural-height
   *  card on every other surface. */
  fillHeight?: boolean;
  /** Cycle 227 (mobile-portrait Now only): wrap the detail content (the
   *  `children` MealContextLines) in a bounded, scrollbar-free, masked
   *  scroll region (`detailScrollClassName` supplies the bound) so tall
   *  content scrolls INSIDE the card instead of clipping or growing it
   *  past the screen. The rings/center/countdown stay fixed above it.
   *  Default false → children render inline, unchanged. */
  detailScroll?: boolean;
  /** Cycle 227: height bound + flex sizing for the detail scroll region. */
  detailScrollClassName?: string;
  /** Cycle 437 (the treating screen's IOB instrument only): render the gauge
   *  at an explicit canvas size, with the ring radii AND stroke scaled
   *  proportionally, and set the centre-stack type explicitly.
   *
   *  ⚠ BOTH HALVES ARE LOAD-BEARING. The shipped centre type is hardcoded px
   *  (total 26 / label 9 / countdown 10) and does NOT scale with `SIZE`, so a
   *  larger canvas ALONE leaves the label + per-ring countdowns at 9px/10px —
   *  below the 13px floor. Conversely, setting the type to 13px at the native
   *  196px canvas overflows the innermost ring (measured: the widest countdown
   *  line puts a corner 56.5px from centre against a 52px inner clear radius).
   *  Growing the rings is what buys the room the bigger type needs.
   *
   *  Default undefined → every existing mount is byte-identical. */
  gauge?: {
    /** Canvas px. Radii + stroke scale by `size / <native size for this ring
     *  count>`, so ring proportions are preserved exactly. */
    size?: number;
    /** Centre IOB total (shipped 26). */
    totalPx?: number;
    /** Centre "I.O.B" / "BASAL" label (shipped 9). */
    labelPx?: number;
    /** Per-ring countdown lines (shipped 10). */
    countdownPx?: number;
    /** Cycle 437: collapse the centre's per-ring countdown lines (one per
     *  rapid dose, plus the basal line in `hideContext`) into ONE line —
     *  the whole-gauge "insulin clears in" time, `iobTimeRemainingMs`.
     *  The per-ring times move OUT to the caller's legend rows, which is
     *  what buys the room for >=13px type at the NATIVE canvas: three
     *  13px lines put a corner 51.8px from centre against a 52.0px inner
     *  clear radius (0.2px of margin, i.e. none), one line puts it at
     *  48.9px (3.1px of margin). Default false -> unchanged. */
    singleCountdown?: boolean;
    /** Cycle 438 (treating screen): align the label + dose-lines column to
     *  the TOP of the card so "Insulin on board" reads as a card header
     *  rather than sitting vertically centred beside the legend.
     *  Default off -> every other mount is byte-identical. */
    contextTop?: boolean;
    /** Cycle 438 (treating screen): extra left padding inside the card so
     *  the gauge does not crowd its own edge. Default off. */
    cardPadLeft?: number;
    /** Cycle 459 (the treating screen's compact step on a phone): the card's
     *  top and bottom padding. Default off → the flat 12 everywhere else. */
    cardPadY?: number;
    /** Cycle 437: the card's own "Insulin on board" side-column label
     *  (shipped 10px — below the 13px floor the treating screen holds). */
    cardLabelPx?: number;
    /** Cycle 437: the BASAL-ONLY centre total (shipped 20px), which is a
     *  different size from the rapid total above. Its own key so one
     *  caller-supplied number cannot silently drive two sizes. */
    basalTotalPx?: number;
  };
}

const STROKE = 8;
const NATIVE_SIZE_3 = 196;
const NATIVE_SIZE_LT3 = 160;

function ringPath(r: number, frac: number): { dasharray: number; dashoffset: number } {
  const C = 2 * Math.PI * r;
  return { dasharray: C, dashoffset: C * (1 - Math.max(0, Math.min(1, frac))) };
}

/** Cycle 104b: the countdown line, to the second — "Xh Mm SSs" /
 *  "Mm SSs" / "SSs" under a minute. Floors at 1s so a ring that's still
 *  drawn never reads "00s". */
function formatTimeLeft(ms: number): string {
  const totalSec = Math.max(1, Math.ceil(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const ss = String(s).padStart(2, "0");
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${ss}s`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
}

export function BolusRings({
  boluses,
  now,
  basal = null,
  onTap,
  onDismissDue,
  onDismissCard,
  children,
  hideContext = false,
  stack = false,
  bare = false,
  cardClassName,
  fillHeight = false,
  detailScroll = false,
  detailScrollClassName,
  gauge,
}: Props) {
  const iob = totalIOB(boluses, now);
  const rapidEmpty = boluses.length === 0;
  const hasBasal = basal != null;
  const isEmpty = rapidEmpty && !hasBasal;
  // Cycle 104b: the countdown resolves to the second — one useNow(1000)
  // for the whole card.
  const timerNow = useNow(1000);
  const timers = boluses
    .map((b) => ({
      ms: iobTimeRemainingMs([b], timerNow),
      // Cycle 343: clamp only the actual ring-color case — the neutral
      // --ink-2 single-bolus fallback is already legible and untouched.
      color: boluses.length > 1 ? ringLabelInk(b.color) : "var(--ink-2)",
      takenAt: b.takenAt,
    }))
    .sort((a, b) => b.takenAt - a.takenAt); // newest-first

  // Cycle 148: the geometry COLLAPSES to the active ring count — a ring
  // (track + arc) is drawn ONLY for a dose that is actually on board, in
  // fixed semantic order outermost→inner: basal → bolus → correction.
  // Radii by count: 1 → the prominent lone ring (canvas 160, r66 — the
  // pre-basal lone-bolus look, whether the one dose is a bolus or a
  // basal); 2 → two nested on the small canvas (160, r66/r50 — so
  // bolus+correction is byte-identical to pre-148); 3 → the cycle-147
  // layout (196, r90/r72/r56). No ghost/empty tracks in any partial
  // state. The EMPTY card (carbs-only context) keeps the shipped
  // cycle-74b dimmed two-track 0.0/I.O.B visual.
  interface RingSpec {
    key: string;
    frac: number;
    color: string;
    opacity: number;
  }
  const ringSpecs: RingSpec[] = [];
  if (hasBasal) {
    // Arc = remainingFrac from the single basal-decay source. A DUE dose
    // has frac 0 (nothing left to draw), so the due state renders a FULL
    // amber circle at reduced opacity — an attention state, not a
    // remaining-time reading. Cycle 148: the color resolves through the
    // single `basalRingColor` helper (normal violet / due-soon warm
    // blend / due amber).
    ringSpecs.push({
      key: "basal",
      frac: basal.due ? 1 : basal.remainingFrac,
      color: basalRingColor(basal),
      opacity: basal.due ? 0.55 : 0.95,
    });
  }
  boluses.forEach((b, i) => {
    ringSpecs.push({
      key: `rapid-${b.id}`,
      frac: remainingFraction(b.takenAt, now),
      // Rapid opacities keep their pre-148 pair values (first 0.95,
      // second 0.7) regardless of which slot they land in.
      color: b.color,
      opacity: i === 0 ? 0.95 : 0.7,
    });
  });
  const ringCount = ringSpecs.length;
  const NATIVE_SIZE = ringCount >= 3 ? NATIVE_SIZE_3 : NATIVE_SIZE_LT3;
  const SIZE = gauge?.size ?? NATIVE_SIZE;
  // Cycle 437: one scale factor drives radii AND stroke, so the ring
  // proportions at a custom canvas are identical to the shipped ones.
  const GAUGE_K = SIZE / NATIVE_SIZE;
  const RING_STROKE = STROKE * GAUGE_K;
  const CX = SIZE / 2;
  const NATIVE_RADII =
    ringCount >= 3 ? [90, 72, 56] : ringCount === 2 ? [66, 50] : [66];
  const RADII = GAUGE_K === 1 ? NATIVE_RADII : NATIVE_RADII.map((r) => r * GAUGE_K);
  // Cycle 437: the shipped centre type is fixed px and does NOT track SIZE.
  const TOTAL_PX = gauge?.totalPx ?? 26;
  const LABEL_PX = gauge?.labelPx ?? 9;
  const COUNTDOWN_PX = gauge?.countdownPx ?? 10;
  const BASAL_TOTAL_PX = gauge?.basalTotalPx ?? 20;
  const CARD_LABEL_PX = gauge?.cardLabelPx ?? 13;
  const CONTEXT_TOP = gauge?.contextTop ?? false;
  const CARD_PAD_LEFT = gauge?.cardPadLeft ?? null;
  const CARD_PAD_Y = gauge?.cardPadY ?? null;

  const [lifted, setLifted] = useState(false);
  const interactive = onTap != null && !isEmpty;
  const activate = () => onTap?.();

  const ringsRow = (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? "View active insulin in the editor" : undefined}
      onClick={interactive ? activate : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                activate();
              }
            }
          : undefined
      }
      onMouseEnter={interactive ? () => setLifted(true) : undefined}
      onMouseLeave={interactive ? () => setLifted(false) : undefined}
      onFocus={interactive ? () => setLifted(true) : undefined}
      onBlur={interactive ? () => setLifted(false) : undefined}
      style={{
        display: "flex",
        flexDirection: stack ? "column" : "row",
        alignItems: stack ? "stretch" : CONTEXT_TOP ? "flex-start" : "center",
        gap: stack ? 12 : 14,
        cursor: interactive ? "pointer" : undefined,
        userSelect: interactive ? "none" : undefined,
        // Cycle 227: in mobile-portrait fill mode the rings row fills +
        // shrinks so the detail region below the (fixed) rings can give up
        // height; min-height:0 lets the shrink propagate to the scroll
        // region. Other surfaces are unaffected (fillHeight defaults false).
        ...(fillHeight && stack ? { flex: "1 1 auto", minHeight: 0 } : null),
      }}
    >
      <div
        style={{
          position: "relative",
          width: SIZE,
          height: SIZE,
          flexShrink: 0,
          alignSelf: stack ? "center" : undefined,
          opacity: isEmpty ? 0.45 : 1,
        }}
      >
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <g transform={`rotate(-90 ${CX} ${CX})`}>
            {isEmpty ? (
              // Empty card (carbs-only context): the shipped cycle-74b
              // dimmed two-track visual — the whole canvas is already at
              // opacity 0.45 via the wrapper.
              <>
                {/* Cycle 437: these two dimmed tracks scale with the
                    canvas like every other ring. Their stroke already did;
                    leaving the radii as literals would have drawn a
                    correctly-thick ring at the wrong diameter the moment a
                    caller passed `gauge.size`. Latent until then — no
                    shipped mount passes one — but wrong. */}
                <circle
                  cx={CX}
                  cy={CX}
                  r={66 * GAUGE_K}
                  fill="none"
                  stroke="var(--bg-sunk)"
                  strokeWidth={RING_STROKE}
                />
                <circle
                  cx={CX}
                  cy={CX}
                  r={50 * GAUGE_K}
                  fill="none"
                  stroke="var(--bg-sunk)"
                  strokeWidth={RING_STROKE}
                />
              </>
            ) : (
              // Cycle 148: one track + arc per PRESENT ring only.
              ringSpecs.map((spec, i) => {
                const r = RADII[i]!;
                const arc = ringPath(r, spec.frac);
                return (
                  <g key={spec.key}>
                    <circle
                      cx={CX}
                      cy={CX}
                      r={r}
                      fill="none"
                      stroke="var(--bg-sunk)"
                      strokeWidth={RING_STROKE}
                    />
                    <circle
                      cx={CX}
                      cy={CX}
                      r={r}
                      fill="none"
                      stroke={spec.color}
                      strokeWidth={RING_STROKE}
                      strokeLinecap="round"
                      strokeDasharray={arc.dasharray}
                      strokeDashoffset={arc.dashoffset}
                      opacity={spec.opacity}
                    />
                  </g>
                );
              })
            )}
          </g>
        </svg>
        {/* Cycle 452.1: the ring centre is the ONE place under the 13 px type
            floor on purpose (operator decision) — its label and countdown must
            fit inside the innermost ring. The type-floor sweep allowlists this
            marker by name; it changes nothing on screen. */}
        <div
          data-floor-exempt="iob-ring-centre"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {rapidEmpty && hasBasal ? (
            // Cycle 147: basal-only — the center shows the long-acting
            // remaining instead of a misleading rapid "0.0 / I.O.B".
            // Cycle 148: the countdown matches the ring's resolved color
            // (violet / due-soon warm blend / due amber).
            <>
              <span
                className="tnum"
                style={{
                  fontFamily: "var(--hero-font)",
                  fontWeight: 600,
                  fontSize: BASAL_TOTAL_PX,
                  letterSpacing: "-0.01em",
                  // Cycle 343: text, not a ring stroke — clamp for contrast.
                  color: ringLabelInk(basalRingColor(basal)),
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {formatBasalHoursLeft(basal.hoursLeft, basal.due)}
              </span>
              <span
                style={{
                  fontSize: LABEL_PX,
                  color: "var(--ink-3)",
                  fontWeight: 600,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  marginTop: 3,
                }}
              >
                Basal
              </span>
            </>
          ) : (
            <>
              <span
                className="tnum"
                style={{
                  fontFamily: "var(--hero-font)",
                  fontWeight: 600,
                  fontSize: TOTAL_PX,
                  letterSpacing: "-0.02em",
                  color: "var(--ink)",
                  lineHeight: 1,
                }}
              >
                {iob.toFixed(1)}
              </span>
              <span
                style={{
                  fontSize: LABEL_PX,
                  color: "var(--ink-3)",
                  fontWeight: 600,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  marginTop: 2,
                }}
              >
                I.O.B
              </span>
              {/* Cycle 104a/b: per-ring rapid countdown(s) centered under
                  the I.O.B label — unchanged by the basal ring. */}
              {!rapidEmpty && gauge?.singleCountdown ? (
                <span
                  className="tnum"
                  style={{
                    marginTop: 3,
                    fontSize: COUNTDOWN_PX,
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    color: "var(--ink-2)",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatTimeLeft(iobTimeRemainingMs(boluses, timerNow))}
                </span>
              ) : null}
              {!rapidEmpty &&
                !gauge?.singleCountdown &&
                timers.map((t, i) => (
                  <span
                    key={t.takenAt}
                    className="tnum"
                    style={{
                      marginTop: i === 0 ? 3 : 2,
                      fontSize: COUNTDOWN_PX,
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      color: t.color,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatTimeLeft(t.ms)}
                  </span>
                ))}
              {/* Cycle 150: in hideContext mode (no dose-lines column —
                  the clock face / landscape rings page) the basal ring
                  gets its countdown in the center too, colored to its
                  ring with a matching dot, so all three rings read a
                  time. The basal stays EXCLUDED from the rapid total +
                  timers above (ring-exclusion invariant); on surfaces
                  with context lines the basal time stays on its
                  Long-acting row and this line never renders. */}
              {hideContext && hasBasal && !gauge?.singleCountdown && (
                <span
                  className="tnum"
                  style={{
                    marginTop: 2,
                    fontSize: COUNTDOWN_PX,
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    // Cycle 343: text, not a ring stroke — clamp for contrast.
                    color: ringLabelInk(basalRingColor(basal)),
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: 999,
                      background: basalRingColor(basal),
                      flexShrink: 0,
                    }}
                  />
                  {formatBasalHoursLeft(basal.hoursLeft, basal.due)}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cycle 115: the side column (label + dose lines) is dropped in
          rings-only mode so the card fits the slim landscape column. */}
      {!hideContext && (
        <div
          style={{
            // Cycle 227: in fill mode the context column fills + shrinks
            // (min-height:0) so the bounded detail scroll region — not the
            // page — absorbs tall content. Otherwise unchanged.
            flex: fillHeight ? "1 1 auto" : stack ? "0 0 auto" : 1,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            minWidth: 0,
            ...(fillHeight ? { minHeight: 0 } : null),
          }}
        >
          <div
            style={{
              flexShrink: 0,
              fontSize: CARD_LABEL_PX,
              color: "var(--ink-3)",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Insulin on board
          </div>
          {/* Cycle 227: the detail region (MealContextLines) scrolls inside
              the card — the "Insulin on board" label + the rings above stay
              fixed; only this region scrolls, with dynamic edge fades and
              no visible scrollbar. */}
          {detailScroll ? (
            <MaskedScroll className={detailScrollClassName}>
              {children}
            </MaskedScroll>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );

  // Cycle 150: bare mode — no card surface at all; the rings sit on the
  // caller's ground (the bedside clock face). Every other mount keeps
  // the shipped card below.
  if (bare) return ringsRow;

  // Cycle 163.1: the dismissible-due glass × — only when the basal is
  // lapsed (due) and the mount opted in. Glass recipe rides the
  // cycle-162 --backbtn-* tokens so it matches the BackButton family.
  const dueDismiss =
    basal?.due && onDismissDue ? (
      <button
        type="button"
        aria-label="Dismiss long-acting due reminder"
        onClick={(e) => {
          e.stopPropagation();
          onDismissDue();
        }}
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 2,
          width: 40,
          height: 40,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 999,
          // Cycle 308: `--glass-x-fill` freezes the ORIGINAL accent-14%
          // recipe — the shared `--backbtn-fill` was re-derived for the
          // BackButton capsule and would have re-tinted these × buttons.
          border: "0.5px solid var(--backbtn-border)",
          background: "var(--glass-x-fill)",
          backdropFilter: "blur(18px) saturate(150%)",
          WebkitBackdropFilter: "blur(18px) saturate(150%)",
          boxShadow: "inset 0 1px 0 var(--backbtn-sheen), var(--backbtn-lift)",
          color: "var(--backbtn-label)",
          fontSize: 20,
          lineHeight: 1,
          cursor: "pointer",
          padding: 0,
        }}
      >
        ×
      </button>
    ) : null;

  // Cycle 303: the whole-card dismiss × (server-coordinated). Same glass
  // recipe as the due-× above, but it hides the ENTIRE IOB card (not just
  // the basal nudge). Mutually exclusive with the due-×: when a basal is
  // DUE the due-nudge × owns the corner, so a due basal can never be
  // dismissed into silence by this control (cycle-121 reasoning).
  //
  // Safety fix (2026-07-15): also withheld while `!rapidEmpty` (real
  // active rapid-acting IOB) — the caller's `iobCardDismissed` now
  // structurally can't hide the card while IOB is active, so offering a
  // × that would silently no-op is confusing; hide the control instead.
  const cardDismiss =
    onDismissCard && rapidEmpty && !(basal?.due && onDismissDue) ? (
      <button
        type="button"
        aria-label="Dismiss insulin-on-board card"
        onClick={(e) => {
          e.stopPropagation();
          onDismissCard();
        }}
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 2,
          width: 40,
          height: 40,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 999,
          // Cycle 308: `--glass-x-fill` freezes the ORIGINAL accent-14%
          // recipe — the shared `--backbtn-fill` was re-derived for the
          // BackButton capsule and would have re-tinted these × buttons.
          border: "0.5px solid var(--backbtn-border)",
          background: "var(--glass-x-fill)",
          backdropFilter: "blur(18px) saturate(150%)",
          WebkitBackdropFilter: "blur(18px) saturate(150%)",
          boxShadow: "inset 0 1px 0 var(--backbtn-sheen), var(--backbtn-lift)",
          color: "var(--backbtn-label)",
          fontSize: 20,
          lineHeight: 1,
          cursor: "pointer",
          padding: 0,
        }}
      >
        ×
      </button>
    ) : null;

  return (
    <div
      // Cycle 227: when `cardClassName` is set (mobile-portrait glass card)
      // the class owns the background + border (a glass panel that the
      // @supports fallback degrades to solid); inline background/border are
      // omitted so the class wins (inline would override a class). Other
      // mounts keep the shipped solid surface exactly.
      className={cardClassName}
      style={{
        position: "relative",
        ...(cardClassName
          ? null
          : {
              // Cycle 86: translucent on the kiosk; solid fallback elsewhere.
              background: "var(--kiosk-surface-fill, var(--bg-elev))",
              border: `0.5px solid ${
                interactive && lifted ? "var(--line-strong)" : "var(--line)"
              }`,
            }),
        borderRadius: 14,
        padding: 12,
        // Cycle 438 (defect 5): the gauge crowded the card's left edge on
        // the treating screen. Opt-in; null keeps the flat 12 everywhere else.
        ...(CARD_PAD_LEFT != null ? { paddingLeft: CARD_PAD_LEFT } : null),
        // Cycle 459: opt-in, for the phone treating screen's compact card.
        ...(CARD_PAD_Y != null ? { paddingTop: CARD_PAD_Y, paddingBottom: CARD_PAD_Y } : null),
        display: "flex",
        flexDirection: "column",
        gap: 10,
        // Cycle 227: fill + shrink within the mobile-portrait flex column,
        // clipping the inner scroll region's masked content to the card.
        ...(fillHeight
          ? { flex: "1 1 auto", minHeight: 0, overflow: "hidden" }
          : null),
        transition: interactive
          ? "border-color 120ms, box-shadow 120ms"
          : undefined,
        boxShadow:
          interactive && lifted ? "0 4px 16px rgba(0, 0, 0, 0.28)" : undefined,
      }}
    >
      {dueDismiss ?? cardDismiss}
      {ringsRow}
    </div>
  );
}
